/**
 * Pure shaping for the network-measurement dashboard (plan §7a). No React,
 * no fetching — everything here is unit-tested in `analyticsShape.test.ts`.
 *
 * Two rules from the guidance doc live here rather than in the components:
 *   • colour follows the ENTITY (a site keeps its slot whatever is filtered);
 *   • a gap is a gap — except `internal.articles_published`, whose collector
 *     writes no row on a day with no publishes, so a missing day is a zero
 *     (plan §2c). Everything else stays null and the line breaks.
 */
import type { SeriesRow } from './api/analytics.js';

/** Site order = categorical slot order. Fixed, never cycled (dataviz rule). */
export const SITE_ORDER = ['orecast', 'bellwire', 'marketfloor', 'assayernetwork'] as const;
export type SiteKey = (typeof SITE_ORDER)[number];

export const SITE_LABEL: Record<string, string> = {
  orecast: 'Orecast',
  bellwire: 'Bell Wire',
  marketfloor: 'The Market Floor',
  assayernetwork: 'Assayer Network',
  // Not a property: the roster row that carries network-wide figures such as
  // the deduplicated hub count (mig 378).
  network_total: 'Network (deduplicated)',
  // Traffic-quality classes (mig 389), drawn as series in their own right.
  multi_page: 'Reader-like (>1.05 pages/visit)',
  single_page: 'Machine-like (\u22641.05 pages/visit)',
  unknown: 'Visits not collected',
};

/** Series that are classes rather than sites, and the colour slot each keeps. */
export const CLASS_SLOT: Record<string, 1 | 2 | 3 | 4> = {
  multi_page: 1, single_page: 3, unknown: 4,
};

/** 1-based categorical slot for a site; unknown sites fold onto slot 4. */
export function siteSlot(site: string): 1 | 2 | 3 | 4 {
  if (CLASS_SLOT[site]) return CLASS_SLOT[site];
  const i = (SITE_ORDER as readonly string[]).indexOf(site);
  return (i >= 0 ? i + 1 : 4) as 1 | 2 | 3 | 4;
}

/** The launch anchor (plan D2). Config, held here until it is served. */
export const LAUNCH_ANCHOR = '2026-07-15';

/** Metrics whose collector writes no row on an empty day (gap == zero). */
export const ZERO_FILLED_METRICS = new Set(['internal.articles_published', 'social.posts']);

export function toNumber(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function ymd(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return ymd(d);
}

/** Every day from `from` to `to` inclusive. */
export function listDays(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export type RangePreset = 'launch' | '7d' | '30d' | '90d' | '12m';

/** Ranges end on `today − 1`: the sweep collects the last COMPLETE UTC day. */
export function rangeForPreset(preset: RangePreset, today: string): { from: string; to: string } {
  const to = addDays(today, -1);
  switch (preset) {
    case '7d': return { from: addDays(to, -6), to };
    case '30d': return { from: addDays(to, -29), to };
    case '90d': return { from: addDays(to, -89), to };
    case '12m': return { from: addDays(to, -364), to };
    case 'launch':
    default: return { from: LAUNCH_ANCHOR, to };
  }
}

/** One chart row: the day plus a value and an estimated flag per site. */
export type DailyPoint = { date: string } & Record<string, number | boolean | string | null>;

export interface DailyShape {
  days: string[];
  sites: string[];
  points: DailyPoint[];
  /** Days (per site) where the stored figure is an extrapolation (F-4). */
  estimatedCount: number;
  /** Latest day any site has a value — the honest "as at". */
  through: string | null;
}

export function estKey(site: string): string {
  return `${site}__est`;
}

/**
 * Shape a total-dimension series into one row per day with a column per site.
 * Sites are ordered by slot; a site with no rows at all is dropped so the
 * legend only names what was measured.
 */
export function shapeDaily(rows: SeriesRow[], from: string, to: string, metricKey: string): DailyShape {
  const zeroFill = ZERO_FILLED_METRICS.has(metricKey);
  const bySiteDay = new Map<string, Map<string, SeriesRow>>();
  for (const r of rows) {
    if (r.dimension_key !== 'total') continue;
    let m = bySiteDay.get(r.site);
    if (!m) { m = new Map(); bySiteDay.set(r.site, m); }
    m.set(r.metric_date, r);
  }
  const present = Array.from(bySiteDay.keys());
  const sites = [
    ...SITE_ORDER.filter((s) => present.includes(s)),
    ...present.filter((s) => !(SITE_ORDER as readonly string[]).includes(s)).sort(),
  ];
  const days = listDays(from, to);
  let estimatedCount = 0;
  let through: string | null = null;
  const points = days.map((date) => {
    const p: DailyPoint = { date };
    for (const s of sites) {
      const r = bySiteDay.get(s)?.get(date);
      if (r) {
        p[s] = toNumber(r.value);
        p[estKey(s)] = !!r.is_estimated;
        if (r.is_estimated) estimatedCount += 1;
        if (!through || date > through) through = date;
      } else {
        p[s] = zeroFill ? 0 : null;
        p[estKey(s)] = false;
      }
    }
    return p;
  });
  return { days, sites, points, estimatedCount, through };
}

/** Network roll-up: the sum across sites, null only when every site is null. */
export function combineSites(shape: DailyShape): DailyShape {
  const points = shape.points.map((p) => {
    let sum: number | null = null;
    let est = false;
    for (const s of shape.sites) {
      const v = p[s];
      if (typeof v === 'number') { sum = (sum ?? 0) + v; est = est || p[estKey(s)] === true; }
    }
    return { date: p.date, network: sum, [estKey('network')]: est } as DailyPoint;
  });
  return { ...shape, sites: ['network'], points };
}

/**
 * `a − b` per site per day (operator 2026-10-13: readers = page views minus
 * the suspected crawler). A site that `b` measured treats a missing day as 0 —
 * Cloudflare returns no row for a zero count; a site `b` never measured gives
 * null, so an uncollected crawler series never reads as "no crawler".
 */
export function subtractShapes(a: DailyShape, b: DailyShape): DailyShape {
  const measured = new Set(b.sites);
  const bByDate = new Map(b.points.map((p) => [p.date, p]));
  const points = a.points.map((p) => {
    const q = bByDate.get(p.date);
    const out: DailyPoint = { date: p.date };
    for (const s of a.sites) {
      const av = p[s];
      const bv = q ? q[s] : null;
      if (typeof av !== 'number' || !measured.has(s)) {
        out[s] = null;
        out[estKey(s)] = false;
        continue;
      }
      out[s] = Math.max(0, av - (typeof bv === 'number' ? bv : 0));
      out[estKey(s)] = p[estKey(s)] === true || (q?.[estKey(s)] === true);
    }
    return out;
  });
  let estimatedCount = 0;
  for (const p of points) for (const s of a.sites) if (p[estKey(s)] === true) estimatedCount += 1;
  return { ...a, points, estimatedCount };
}

/** Restrict a shape to a sub-range (the charts show the selected range; the
 *  headline tiles read trailing windows off the same fetch). */
export function sliceDays(shape: DailyShape, from: string, to: string): DailyShape {
  const points = shape.points.filter((p) => p.date >= from && p.date <= to);
  return { ...shape, days: points.map((p) => p.date), points };
}

export interface WindowTotal {
  value: number | null;
  /** Site totals in slot order, for the tile's per-site line. */
  bySite: Array<{ site: string; value: number | null }>;
  daysWithData: number;
  estimatedDays: number;
  from: string;
  to: string;
}

/** Sum over the trailing `n` days ending at the latest measured day. */
export function trailingTotal(shape: DailyShape, n: number, offsetDays = 0): WindowTotal {
  // offsetDays shifts the window back: trailingTotal(s, 30, 30) is the 30 days
  // before the current 30 — the comparison window for a trend.
  const latest = shape.through ?? shape.days[shape.days.length - 1] ?? '';
  const to = latest ? addDays(latest, -offsetDays) : '';
  const from = to ? addDays(to, -(n - 1)) : '';
  const rows = shape.points.filter((p) => p.date >= from && p.date <= to);
  let any = false;
  let estimatedDays = 0;
  const daysWithData = rows.filter((p) => shape.sites.some((s) => typeof p[s] === 'number')).length;
  const bySite = shape.sites.map((site) => {
    let t: number | null = null;
    for (const p of rows) {
      const v = p[site];
      if (typeof v === 'number') { t = (t ?? 0) + v; any = true; if (p[estKey(site)] === true) estimatedDays += 1; }
    }
    return { site, value: t };
  });
  const value = any ? bySite.reduce((a, b) => a + (b.value ?? 0), 0) : null;
  return { value, bySite, daysWithData, estimatedDays, from, to };
}

/** Latest value per site — for point-in-time metrics (hub count). */
export function latestBySite(shape: DailyShape): WindowTotal {
  const bySite = shape.sites.map((site) => {
    for (let i = shape.points.length - 1; i >= 0; i -= 1) {
      const v = shape.points[i][site];
      if (typeof v === 'number') return { site, value: v };
    }
    return { site, value: null };
  });
  const any = bySite.some((b) => b.value != null);
  return {
    value: any ? bySite.reduce((a, b) => a + (b.value ?? 0), 0) : null,
    bySite,
    daysWithData: any ? 1 : 0,
    estimatedDays: 0,
    from: shape.through ?? '',
    to: shape.through ?? '',
  };
}

/** Point-in-time value as it stood on `asOf` (latest per site on or before it). */
export function valueAsOf(shape: DailyShape, asOf: string): number | null {
  let total: number | null = null;
  for (const site of shape.sites) {
    for (let i = shape.points.length - 1; i >= 0; i -= 1) {
      const p = shape.points[i];
      if (p.date > asOf) continue;
      const v = p[site];
      if (typeof v === 'number') { total = (total ?? 0) + v; break; }
    }
  }
  return total;
}

// ---------------------------------------------------------------------------
// Trend indicator (operator 2026-10-06): up / stable / down against the
// previous window of the same length. "Stable" is a band, never equality:
// within ±STABLE_PCT of the previous value, or within √previous for small
// counts (the Poisson noise of a count — 8 → 10 searches is noise, not a
// rise). The wider of the two applies.
// ---------------------------------------------------------------------------
export const STABLE_PCT = 0.05;

export type TrendDirection = 'up' | 'flat' | 'down';

export interface Trend {
  direction: TrendDirection;
  current: number;
  previous: number;
  /** (current − previous) ÷ previous */
  pct: number;
  /** half-width of the stable band, in the metric's units */
  band: number;
  /** e.g. "previous 30 days" */
  against: string;
}

/** null when there is nothing honest to compare: no current or previous
 *  value, a previous of 0, or a previous window not fully measured. */
export function trendOf(
  current: number | null | undefined,
  previous: number | null | undefined,
  against: string,
  previousComplete = true,
): Trend | null {
  if (current == null || previous == null || !previousComplete || previous <= 0) return null;
  const band = Math.max(STABLE_PCT * previous, Math.sqrt(previous));
  const delta = current - previous;
  const direction: TrendDirection = delta > band ? 'up' : delta < -band ? 'down' : 'flat';
  return { direction, current, previous, pct: delta / previous, band, against };
}

/** Trend for a trailing-window sum: this n days vs the n days before. */
export function windowTrend(shape: DailyShape | undefined, n: number): Trend | null {
  if (!shape) return null;
  const cur = trailingTotal(shape, n);
  const prev = trailingTotal(shape, n, n);
  return trendOf(cur.value, prev.value, `previous ${n} days`, prev.daysWithData >= n);
}

/** Why a card shows no trend badge — stated on the card (operator 2026-10-06),
 *  or null when a badge is shown or there is no data at all. */
export function noTrendReason(
  current: number | null | undefined,
  previous: number | null | undefined,
  previousComplete: boolean,
  against: string,
): string | null {
  if (current == null) return null;
  if (!previousComplete) return `no trend: the ${against} are not fully measured yet`;
  if (previous == null || previous <= 0) return `no trend: the ${against} were 0`;
  return null;
}

/** windowTrend's reason when it returns null. */
export function windowNoTrendReason(shape: DailyShape | undefined, n: number): string | null {
  if (!shape) return null;
  const cur = trailingTotal(shape, n);
  const prev = trailingTotal(shape, n, n);
  return noTrendReason(cur.value, prev.value, prev.daysWithData >= n, `previous ${n} days`);
}

/** Auto-compact figures for tiles: 1,284 / 12.9K / 4.2M (dataviz stat-tile contract). */
export function compact(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const one = (x: number) => x.toFixed(1).replace(/\.0$/, '');
  if (abs >= 1_000_000) return `${one(n / 1_000_000)}M`;
  if (abs >= 10_000) return `${one(n / 1_000)}K`;
  return Math.round(n).toLocaleString();
}

/** The last `n` day values of a single-series shape, for a sparkline. */
export function sparkline(shape: DailyShape, n: number): Array<{ date: string; v: number | null }> {
  const combined = shape.sites.length === 1 ? shape : combineSites(shape);
  const key = combined.sites[0];
  return combined.points.slice(-n).map((p) => ({ date: p.date, v: typeof p[key] === 'number' ? (p[key] as number) : null }));
}

/** Top-N breakdown over a dimension, summed across the fetched days, with a
 *  per-site split. Used for the crawler table. */
export function topDimension(rows: SeriesRow[], n: number): Array<{ key: string; total: number; bySite: Record<string, number>; estimated: boolean }> {
  const acc = new Map<string, { total: number; bySite: Record<string, number>; estimated: boolean }>();
  for (const r of rows) {
    if (r.dimension_key === 'total') continue;
    const v = toNumber(r.value) ?? 0;
    const e = acc.get(r.dimension_value) ?? { total: 0, bySite: {}, estimated: false };
    e.total += v;
    e.bySite[r.site] = (e.bySite[r.site] ?? 0) + v;
    e.estimated = e.estimated || !!r.is_estimated;
    acc.set(r.dimension_value, e);
  }
  return Array.from(acc.entries())
    .map(([key, e]) => ({ key, ...e }))
    .sort((a, b) => b.total - a.total)
    .slice(0, n);
}

/**
 * One platform's slice of a per-platform series, re-keyed as a total series
 * so it shapes and charts like any other (X impressions are stored as
 * `social.impressions` dimension platform=twitter).
 */
/**
 * Re-key a breakdown so each dimension VALUE becomes its own series, summed
 * across sites — for a chart whose lines are classes, not properties (the
 * traffic-quality split, mig 389).
 */
export function rowsAsSeriesByDimension(rows: SeriesRow[]): SeriesRow[] {
  const acc = new Map<string, SeriesRow>();
  for (const r of rows) {
    if (r.dimension_key === 'total') continue;
    const key = `${r.dimension_value}|${r.metric_date}`;
    const prev = acc.get(key);
    const v = toNumber(r.value) ?? 0;
    if (prev) prev.value = (toNumber(prev.value) ?? 0) + v;
    else acc.set(key, { ...r, site: r.dimension_value, dimension_key: 'total', dimension_value: '', value: v });
  }
  return Array.from(acc.values());
}

export function rowsForDimensionValue(rows: SeriesRow[], value: string): SeriesRow[] {
  return rows
    .filter((r) => r.dimension_value === value)
    .map((r) => ({ ...r, dimension_key: 'total', dimension_value: '' }));
}

// ---------------------------------------------------------------- clean axes

/** Round up to 1 / 2 / 2.5 / 5 × 10^k — a gridline-friendly ceiling. */
export function niceCeil(v: number): number {
  if (v <= 0) return 0;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

/**
 * The y-axis ceiling for a chart with an outlier, or null when the data
 * needs none. One spike (a viral post, a crawler burst) otherwise flattens
 * every other day against the floor. Rule: with at least 14 positive values,
 * if the largest is more than 2.5x the 95th percentile, cap the axis at a
 * nice ceiling above 1.25x that percentile. The days above it are drawn at
 * the top edge with a marker, and their true values stay in the tooltip and
 * the table — clipped on the axis, never altered.
 */
export function robustCeiling(shape: DailyShape): number | null {
  const vals: number[] = [];
  for (const p of shape.points) {
    for (const s of shape.sites) {
      const v = p[s];
      if (typeof v === 'number' && v > 0) vals.push(v);
    }
  }
  if (vals.length < 14) return null;
  vals.sort((a, b) => a - b);
  const p95 = vals[Math.min(vals.length - 1, Math.floor(0.95 * (vals.length - 1)))];
  const max = vals[vals.length - 1];
  if (p95 <= 0 || max <= 2.5 * p95) return null;
  return niceCeil(p95 * 1.25);
}

export function rawKey(site: string): string {
  return `${site}__raw`;
}

export interface DisplayShape extends DailyShape {
  ceiling: number | null;
  /** Site-days drawn at the ceiling because their value is above it. */
  clippedHigh: number;
  /** Site-days below zero drawn at zero (e.g. a day of net unfollows). */
  clampedNegative: number;
}

/**
 * The chart's copy of a shape: values clamped to [0, ceiling] so the axis
 * stays pinned at zero and a spike cannot flatten the rest; the true value
 * is kept under `<site>__raw` wherever it was changed.
 */
export function displayShape(shape: DailyShape, ceiling: number | null = robustCeiling(shape)): DisplayShape {
  let clippedHigh = 0;
  let clampedNegative = 0;
  const points = shape.points.map((p) => {
    const q: DailyPoint = { ...p };
    for (const s of shape.sites) {
      const v = p[s];
      if (typeof v !== 'number') continue;
      if (v < 0) { q[s] = 0; q[rawKey(s)] = v; clampedNegative += 1; }
      else if (ceiling != null && v > ceiling) { q[s] = ceiling; q[rawKey(s)] = v; clippedHigh += 1; }
    }
    return q;
  });
  return { ...shape, points, ceiling, clippedHigh, clampedNegative };
}

// ---------------------------------------------------------------- collection coverage

export interface MetricCoverage {
  metric_key: string;
  earliest: string | null;
  first_activity: string | null;
  latest: string | null;
  days: number;
}

/** The source families the coverage table reads in, and why each starts where it does. */
export const COVERAGE_FAMILIES: Array<{ key: string; label: string; match: (k: string) => boolean; why: string }> = [
  { key: 'gsc', label: 'Search Console', match: (k) => k.startsWith('gsc.'), why: 'Google keeps 16 months; backfilled on day one' },
  { key: 'li_series', label: 'LinkedIn — gains, Page views, post stats', match: (k) => /^li\.(followers\.gained|page\.(views|unique_views)|shares\.)/.test(k) && !k.endsWith('_month'), why: 'The API gives 12 months back; raw rows kept one year' },
  { key: 'li_snap', label: 'LinkedIn — follower totals and demographics', match: (k) => /^li\.(followers\.(total|demographic|association)|page\.views_demographic)$/.test(k), why: 'Snapshots only: no history, collected daily from the first run' },
  { key: 'zone', label: 'Cloudflare zone traffic', match: (k) => k.startsWith('cf.zone.'), why: 'Cloudflare keeps 365 days of zone totals' },
  { key: 'rum', label: 'Cloudflare beacon (human page views)', match: (k) => k.startsWith('rum.'), why: 'Unsampled for 7 days, ~10% samples after — older days are estimates' },
  { key: 'crawlers', label: 'AI and search crawlers', match: (k) => k.startsWith('cf.ai_crawler') || k.startsWith('cf.crawler'), why: 'Cloudflare keeps 8 days; history exists only from our first collection' },
  { key: 'social', label: 'Buffer social', match: (k) => k.startsWith('social.'), why: 'Buffer engagement is read after posts are sent' },
  { key: 'articles', label: 'Articles published', match: (k) => k === 'internal.articles_published', why: 'Our own publishing record' },
  { key: 'hubs', label: 'Company hubs', match: (k) => k === 'internal.company_hubs', why: 'A daily count, recorded from its first run' },
];

export interface CoverageRow {
  key: string;
  label: string;
  why: string;
  storedFrom: string | null;
  activityFrom: string | null;
  through: string | null;
}

const minDay = (a: string | null, b: string | null) => (a == null ? b : b == null ? a : a < b ? a : b);
const maxDay = (a: string | null, b: string | null) => (a == null ? b : b == null ? a : a > b ? a : b);

/** Roll per-metric coverage up to the source families, in table order. */
export function coverageByFamily(metrics: MetricCoverage[]): CoverageRow[] {
  return COVERAGE_FAMILIES.map((f) => {
    const own = metrics.filter((m) => f.match(m.metric_key));
    return {
      key: f.key, label: f.label, why: f.why,
      storedFrom: own.reduce<string | null>((a, m) => minDay(a, m.earliest), null),
      activityFrom: own.reduce<string | null>((a, m) => minDay(a, m.first_activity), null),
      through: own.reduce<string | null>((a, m) => maxDay(a, m.latest), null),
    };
  }).filter((r) => r.storedFrom != null);
}
